import React, { useState, useRef } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  InputAdornment,
  IconButton,
  Link,
  CircularProgress,
} from "@mui/material";
import {
  EmailOutlined,
  LockOutlined,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import { useForm, Controller } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import ForgotPasswordViewController from "../../redux/actions/ForgotPasswordApiAction";
import AuthLayout from "../../components/auth/AuthLayout";
import {
  BRAND,
  inputSx,
  primaryButtonSx,
  otpInputSx,
} from "../../components/auth/authTheme";

const OtpFields = ({ control, setValue, setError, clearErrors }) => {
  const inputs = useRef([]);

  const fillOtpDigits = (digits, startIndex = 0) => {
    digits.split("").forEach((digit, offset) => {
      const index = startIndex + offset;
      if (index < 6) {
        setValue(`otp${index}`, digit, { shouldValidate: true });
      }
    });
    const nextIndex = Math.min(startIndex + digits.length, 5);
    inputs.current[nextIndex]?.focus();
  };

  const handleChange = (e, index, field) => {
    const digits = e.target.value.replace(/\D/g, "");
    if (!digits) {
      field.onChange("");
      return;
    }

    if (digits.length > 1) {
      fillOtpDigits(digits, index);
      return;
    }

    field.onChange(digits);
    clearErrors(`otp${index}`);
    if (index < 5) inputs.current[index + 1]?.focus();
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !e.target.value && index > 0) {
      inputs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e, index) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (!pasted) return;
    fillOtpDigits(pasted, index);
  };

  return (
    <Box display="flex" justifyContent="center" gap={1} mb={3}>
      {Array.from({ length: 6 }).map((_, i) => (
        <Controller
          key={i}
          name={`otp${i}`}
          control={control}
          defaultValue=""
          rules={{
            required: "Required",
            pattern: { value: /^\d$/, message: "Invalid" },
          }}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              inputRef={(el) => (inputs.current[i] = el)}
              onChange={(e) => handleChange(e, i, field)}
              onKeyDown={(e) => handleKeyDown(e, i)}
              onPaste={(e) => handlePaste(e, i)}
              inputProps={{
                maxLength: 6,
                inputMode: "numeric",
                autoComplete: i === 0 ? "one-time-code" : "off",
              }}
              variant="outlined"
              error={!!fieldState.error}
              sx={otpInputSx}
            />
          )}
        />
      ))}
    </Box>
  );
};

const ForgotPassword = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { loading } = useSelector((state) => state.forgotPassword || {});

  const {
    control,
    handleSubmit,
    getValues,
    formState: { errors },
    reset,
    watch,
    setValue,
    setError,
    clearErrors,
  } = useForm({ mode: "onSubmit" });

  const [step, setStep] = useState("email");
  const [showPassword, setShowPassword] = useState(false);
  const [timer, setTimer] = useState(30);
  const [showResend, setShowResend] = useState(false);
  const [emailForReset, setEmailForReset] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const { forgotPassword, resetOtpPassword, resendOtp } =
    ForgotPasswordViewController();

  const password = watch("password");
  const confirmPassword = watch("confirmPassword");

  const startResendTimer = () => {
    setShowResend(false);
    setTimer(30);
    const interval = setInterval(() => {
      setTimer((prev) => {
        if (prev === 1) {
          clearInterval(interval);
          setShowResend(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const requestOtp = async ({ identifier }) => {
    setSubmitting(true);
    try {
      await dispatch(forgotPassword({ identifier }));
      setEmailForReset(identifier.trim().toLowerCase());
      setStep("otp");
      startResendTimer();
    } catch (error) {
      console.error("OTP request failed", error);
    } finally {
      setSubmitting(false);
    }
  };

  const resetPassword = async (data) => {
    const otp = Array.from({ length: 6 }, (_, i) => data[`otp${i}`] || "").join("");

    if (otp.length !== 6) {
      toast.error("Please enter the complete 6-digit OTP.");
      return;
    }

    if (!data.password || data.password.length < 6) {
      toast.error("Password must be at least 6 characters.");
      return;
    }

    if (data.password !== data.confirmPassword) {
      toast.error("Passwords do not match.");
      return;
    }

    if (!emailForReset) {
      toast.error("Session expired. Please request a new OTP.");
      setStep("email");
      return;
    }

    setSubmitting(true);
    try {
      await dispatch(
        resetOtpPassword({
          identifier: emailForReset,
          otp,
          password: data.password,
        })
      );
      reset();
      navigate("/login");
    } catch (error) {
      console.error("Reset password failed", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleResetSubmit = (event) => {
    event.preventDefault();
    resetPassword(getValues());
  };

  const handleResendOtp = async () => {
    if (!emailForReset) return;

    try {
      await dispatch(resendOtp({ identifier: emailForReset }));
      startResendTimer();
    } catch (error) {
      console.error("Resend OTP failed:", error);
    }
  };

  const onInvalid = (formErrors) => {
    if (formErrors.identifier) {
      toast.error(formErrors.identifier.message || "Enter a valid email.");
      return;
    }
    if (step === "otp") {
      const otpMissing = Array.from({ length: 6 }, (_, i) => formErrors[`otp${i}`]).some(
        Boolean
      );
      if (otpMissing) {
        toast.error("Please enter the complete 6-digit OTP.");
      } else if (formErrors.password) {
        toast.error(formErrors.password.message || "Enter a valid password.");
      } else if (formErrors.confirmPassword) {
        toast.error(
          formErrors.confirmPassword.message || "Please confirm your password."
        );
      }
    }
  };

  const isBusy = Boolean(loading || submitting);

  return (
    <AuthLayout
      title={step === "email" ? "Forgot password?" : "Reset your password"}
      subtitle={
        step === "email"
          ? "No worries, we'll send you reset instructions."
          : "Enter the 6-digit code sent to your email."
      }
    >
      {step === "email" ? (
        <form onSubmit={handleSubmit(requestOtp, onInvalid)} noValidate>
          <Controller
            name="identifier"
            control={control}
            rules={{
              required: "Email is required",
              pattern: {
                value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                message: "Enter a valid email",
              },
            }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Email Address"
                fullWidth
                error={!!errors.identifier}
                helperText={errors.identifier?.message}
                variant="outlined"
                autoComplete="email"
                sx={{ ...inputSx, mb: 3 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailOutlined />
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disableElevation
            disabled={isBusy}
            sx={{
              ...primaryButtonSx,
              mb: 2,
              "&.Mui-disabled": {
                bgcolor: isBusy ? BRAND.orange : undefined,
                color: isBusy ? "#fff" : undefined,
                opacity: isBusy ? 0.85 : undefined,
              },
            }}
            startIcon={
              isBusy ? <CircularProgress size={18} color="inherit" /> : null
            }
          >
            {isBusy ? "Sending OTP..." : "Get OTP"}
          </Button>

          <Box sx={{ textAlign: "center" }}>
            <Link
              component="button"
              type="button"
              variant="body2"
              underline="hover"
              onClick={() => navigate("/login")}
              sx={{
                color: BRAND.orange,
                fontWeight: 600,
                cursor: "pointer",
                border: "none",
                background: "none",
                fontFamily: "inherit",
              }}
            >
              Back to sign in
            </Link>
          </Box>
        </form>
      ) : (
        <form onSubmit={handleResetSubmit} noValidate>
          <OtpFields
            control={control}
            setValue={setValue}
            setError={setError}
            clearErrors={clearErrors}
          />

          <Controller
            name="password"
            control={control}
            rules={{
              required: "Password is required",
              minLength: { value: 6, message: "Min 6 characters required" },
            }}
            render={({ field }) => (
              <TextField
                {...field}
                label="New Password"
                type={showPassword ? "text" : "password"}
                fullWidth
                error={!!errors.password}
                helperText={errors.password?.message}
                sx={{ ...inputSx, mb: 2.5 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlined />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        sx={{ color: BRAND.muted }}
                      >
                        {showPassword ? <Visibility /> : <VisibilityOff />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />

          <Controller
            name="confirmPassword"
            control={control}
            rules={{
              required: "Please confirm password",
              validate: (value) =>
                value === password || "Passwords do not match",
            }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Confirm Password"
                type={showPassword ? "text" : "password"}
                fullWidth
                error={!!errors.confirmPassword}
                helperText={errors.confirmPassword?.message}
                sx={{ ...inputSx, mb: 3 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlined />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        sx={{ color: BRAND.muted }}
                      >
                        {showPassword ? <Visibility /> : <VisibilityOff />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disableElevation
            disabled={isBusy}
            sx={{
              ...primaryButtonSx,
              mb: 2,
              "&.Mui-disabled": {
                bgcolor: isBusy ? BRAND.orange : undefined,
                color: isBusy ? "#fff" : undefined,
                opacity: isBusy ? 0.85 : undefined,
              },
            }}
            startIcon={
              isBusy ? <CircularProgress size={18} color="inherit" /> : null
            }
          >
            {isBusy ? "Resetting..." : "Reset Password"}
          </Button>

          <Typography
            variant="body2"
            align="center"
            sx={{ color: BRAND.muted, mb: 1.5 }}
          >
            Didn't receive the OTP?{" "}
            {showResend ? (
              <Box
                component="span"
                sx={{ color: BRAND.orange, fontWeight: 600, cursor: "pointer" }}
                onClick={handleResendOtp}
              >
                Resend OTP
              </Box>
            ) : (
              <Box component="span" sx={{ fontWeight: 600 }}>
                Resend in {timer}s
              </Box>
            )}
          </Typography>

          <Box sx={{ textAlign: "center" }}>
            <Link
              component="button"
              type="button"
              variant="body2"
              underline="hover"
              onClick={() => navigate("/login")}
              sx={{
                color: BRAND.orange,
                fontWeight: 600,
                cursor: "pointer",
                border: "none",
                background: "none",
                fontFamily: "inherit",
              }}
            >
              Back to sign in
            </Link>
          </Box>
        </form>
      )}
    </AuthLayout>
  );
};

export default ForgotPassword;
